import type { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from "@reduxjs/toolkit/query"
import { fetchBaseQuery } from "@reduxjs/toolkit/query"
import { v4 as uuidv4 } from "uuid"
import { getAuthStrategy } from "../utils"

declare global {
  interface Window {
    Auth0?: { getAccessTokenSilently: () => Promise<string> }
  }
}

// Read environment variables
const VITE_SL2_API_V2_URL = import.meta.env.VITE_SL2_API_V2_URL
// For parent/embedded strategy: only the path is configured; the origin is derived from the parent window
const VITE_SL2_API_EMBEDDED_PATH = import.meta.env.VITE_SL2_API_EMBEDDED_PATH

// Determine strategy and base URL at module load time
const strategy = getAuthStrategy()

function resolveApiUrl(): string {
  if (strategy === "auth0") return VITE_SL2_API_V2_URL
  // parent strategy: derive origin from parent, append configured path
  const parentOrigin = window.parent !== window
    ? new URL(document.referrer).origin
    : window.location.origin
  return parentOrigin + (VITE_SL2_API_EMBEDDED_PATH || "/sl2-api")
}

/**
 * The resolved API origin+path every SL2 request is built on.
 *
 * Exported (LOCKER-355) so the SA locker console can PRINT the exact URL it
 * read a payload from. When the page and a manual `curl` disagree, the only
 * thing that settles it is which endpoint each one actually hit — and that was
 * knowable from the code alone. Read-only; nothing outside this module builds a
 * request from it.
 */
export const sl2ApiUrl = resolveApiUrl()

// Only validate the URL required for the active strategy
if (!sl2ApiUrl) {
  throw new Error(
    `Missing API URL for auth strategy "${strategy}". ` +
      (strategy === "parent"
        ? "Set VITE_SL2_API_EMBEDDED_PATH"
        : "Set VITE_SL2_API_V2_URL"),
  )
}

// Thrown by prepareAuthHeaders when no token can be obtained for a strategy
// that requires one. Caught in the outer baseQuery to short-circuit the request.
class MissingAuthTokenError extends Error {
  constructor(public readonly reason: string) {
    super(reason)
    this.name = "MissingAuthTokenError"
  }
}

async function prepareAuthHeaders(headers: Headers) {
  headers.set("idempotency-key", uuidv4())
  headers.set("ts", Date.now().toString())
  headers.set("Mbls-Sl2-Wfe-Auth-Strategy", strategy)

  if (strategy === "auth0") {
    let token: string | null | undefined
    try {
      token = await window.Auth0?.getAccessTokenSilently()
    } catch (error) {
      throw new MissingAuthTokenError(`Auth0 getAccessTokenSilently threw: ${(error as Error)?.message ?? error}`)
    }
    if (!token) throw new MissingAuthTokenError("No Auth0 token available")
    headers.set("authorization", `Bearer ${token}`)
  }
  // For parent strategy, no token needed — cookies are sent via credentials: "include"
  return headers
}

const rawBaseQuery = fetchBaseQuery({
  baseUrl: sl2ApiUrl,
  // Parent strategy uses cookie-based auth — must include credentials
  ...(strategy === "parent" && {
    credentials: "include" as RequestCredentials,
  }),
  prepareHeaders: async (headers) => {
    return prepareAuthHeaders(headers)
  },
})

// Mutex: prevents thundering-herd when multiple API calls 401 simultaneously
let refreshPromise: Promise<void> | null = null

async function refreshToken(): Promise<void> {
  if (strategy === "auth0") {
    // Auth0 SDK refreshes the token automatically via getAccessTokenSilently()
    await window.Auth0?.getAccessTokenSilently()
  }
}

function syntheticUnauthorized(reason: string): { error: FetchBaseQueryError } {
  console.error("[sl2-baseQuery] short-circuiting request:", reason)
  return {
    error: {
      status: 401,
      data: { code: "MISSING_AUTH_TOKEN", reason },
    } as FetchBaseQueryError,
  }
}

async function runRawBaseQuery(
  args: string | FetchArgs,
  api: Parameters<typeof rawBaseQuery>[1],
  extraOptions: Parameters<typeof rawBaseQuery>[2],
) {
  // fetchBaseQuery does not consistently catch throws from prepareHeaders,
  // so we guard the call here and convert MissingAuthTokenError into a 401.
  try {
    return await rawBaseQuery(args, api, extraOptions)
  } catch (error) {
    if (error instanceof MissingAuthTokenError) {
      return syntheticUnauthorized(error.reason)
    }
    throw error
  }
}

export const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}, FetchBaseQueryMeta> = async (args, api, extraOptions) => {
  const result = await runRawBaseQuery(args, api, extraOptions)

  if (result.error?.status !== 401) {
    return result
  }

  // 401 — attempt one token refresh (coalesced across concurrent calls)
  if (!refreshPromise) {
    refreshPromise = refreshToken().finally(() => {
      refreshPromise = null
    })
  }

  try {
    await refreshPromise
  } catch {
    // Refresh failed — return the 401 error as-is.
    // meSlice matchRejected will set isMblsAuthenticated=false → route protection handles redirect.
    return result
  }

  // Retry the original request with the refreshed token
  return runRawBaseQuery(args, api, extraOptions)
}

// native fetch version with prepared headers
export const fetchSl2Api = async (input: RequestInfo, init?: RequestInit) => {
  const headers = new Headers(init?.headers)
  await prepareAuthHeaders(headers)

  const fetchInit: RequestInit = {
    ...init,
    headers,
    ...(strategy === "parent" && { credentials: "include" }),
  }

  // Prepend base URL for string inputs
  const url = typeof input === "string" ? sl2ApiUrl + input : input
  return fetch(url, fetchInit)
}

/**
 * Turn whatever `unwrap()` rejected with into something a human can read.
 *
 * RTK Query does NOT reject with an `Error`: it rejects with a
 * `FetchBaseQueryError` (`{ status, data }`, or `{ status: "FETCH_ERROR",
 * error }`) or a `SerializedError`. Neither has `.message`, so the obvious
 * `error?.message ?? String(error)` renders the literal string
 * "[object Object]" — which is what the reservation-sync report showed instead
 * of naming a CORS failure, and cost a diagnosis.
 */
export const describeQueryError = (error: unknown): string => {
  if (!error) return "unknown error"
  if (error instanceof Error) return error.message
  if (typeof error === "string") return error
  const e = error as Record<string, any>

  // Transport-level: the request never produced an HTTP response. A blocked
  // CORS preflight and a dead host both land here, so say so explicitly.
  if (e.status === "FETCH_ERROR")
    return `network or CORS failure — the response never reached the browser${
      e.error ? ` (${e.error})` : ""
    }`
  if (e.status === "PARSING_ERROR")
    return `the server returned a body that is not JSON (HTTP ${e.originalStatus})`
  if (e.status === "TIMEOUT_ERROR") return "the request timed out"
  if (e.status === "CUSTOM_ERROR") return e.error ?? "request failed"

  // HTTP-level: prefer the API's own message over a bare status code.
  const body = e.data
  const fromBody =
    typeof body === "string"
      ? body
      : (body?.message ?? body?.errorMessage ?? body?.error)
  if (typeof e.status === "number")
    return fromBody ? `HTTP ${e.status} — ${fromBody}` : `HTTP ${e.status}`

  return e.message ?? fromBody ?? JSON.stringify(error)
}
